import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import TextField from '../TextField/TextField';
import { CountryFieldAutoComplete, StateFieldAutoComplete } from '../Autocomplete/Autocomplete';
import USStates from '../../../../../data/USStates';
import CanadaStates from '../../../../../data/CanadaStates';
import Countries from '../../../../../data/Countries';
import SplitColumnWrapper from '../../SplitColumnWrapper/SplitColumnWrapper';
import { Stack, Typography } from '@mui/material';
import { SubHeading } from '../../../PageHeadings/PageHeadings';
import { useFormContext, useWatch } from 'react-hook-form';
import { Map, AdvancedMarker, Pin } from '@vis.gl/react-google-maps';
import { debounce } from 'lodash';
import useGoogleMapsLibrary from '../../../../../hooks/useGoogleMapsLibrary';

interface AddressFieldProps {

  name: string | undefined;

  label: string;

  addressBook?: boolean;

  showGoogleMapValidation?: boolean;

  showPhone?: boolean;

  showValidateMapWarning?: boolean;

}

const AddressField: FC<AddressFieldProps> = ({ name, label, addressBook = false, showGoogleMapValidation = true, showPhone = false, showValidateMapWarning = false }: AddressFieldProps) => {

  const { getValues, setValue, resetField } = useFormContext();

  /*
    watching form values to populate into Google geocoding api
    service to validate the address
  */

  const initCountry = useMemo(() => getValues(`${name ? `${name}.country` : 'country'}`), [name, getValues]);

  const countryCode = useWatch({ name: `${name ? `${name}.countryCode` : 'countryCode'}` });

  const street = useWatch({ name: `${name ? `${name}.street` : 'street'}` });

  const street2 = useWatch({ name: `${name ? `${name}.street2` : 'street2'}` });

  const city = useWatch({ name: `${name ? `${name}.city` : 'city'}` });

  const state = useWatch({ name: `${name ? `${name}.state` : 'state'}` });

  const stateCode = useWatch({ name: `${name ? `${name}.stateCode` : 'stateCode'}` });

  const zip = useWatch({ name: `${name ? `${name}.zip` : 'zip'}` });

  const country = useWatch({ name: `${name ? `${name}.country` : 'country'}` });

  const [geoCodingResult, setGeocodingResult] = useState<google.maps.GeocoderResult>();

  const { geocodeAddress } = useGoogleMapsLibrary();

  const [pois, setPois] = useState<Poi[]>([]);

  useEffect(() => {

    if (country) {

      if (country === 'United States' || country === 'Canada' || countryCode === 'US' || countryCode === 'CA') {

        let validateStateSelection = (countryCode === 'US' || country === 'United States') ? USStates.some((s: any) => s.value[1] === stateCode) : CanadaStates.some((s: any) => s.value[1] === stateCode)

        if (initCountry !== country && !validateStateSelection) {

          setValue(`${name ? `${name}.state` : 'state'}`, undefined);

          setValue(`${name ? `${name}.stateCode` : 'stateCode'}`, undefined);

          resetField(`${name ? `${name}.state` : 'state'}`, { defaultValue: '' });

          resetField(`${name ? `${name}.stateCode` : 'stateCode'}`, { defaultValue: '' });

        }

      } else {

        setValue(`${name ? `${name}.state` : 'state'}`, country);

        setValue(`${name ? `${name}.stateCode` : 'stateCode'}`, countryCode);

      }

    }

  }, [name, initCountry, country, countryCode, stateCode, resetField, setValue]);


  /*
    On load check if a country is populated into
    the form field on component mount If not we want to populate based
    on environment variable default country
  */
  useEffect(() => {

    if (!getValues(`${name ? `${name}.countryCode` : 'countryCode'}`) && !getValues(`${name ? `${name}.country` : 'country'}`)) {

      Countries.forEach((country) => {

        if (Array.isArray(country.value)) {

          const countryFound = country.value.includes(process.env.REACT_APP_DEFAULT_COUNTRY ?? 'US');

          if (countryFound) {

            setValue(`${name ? `${name}.country` : 'country'}`, country.value[0]);

            setValue(`${name ? `${name}.countryCode` : 'countryCode'}`, country.value[1]);

          }

        }

      });

    }

  }, [name, getValues, setValue]);

  /*
    Handles validating the address via google geocoding services
    we append the lat lng and formatted_address fields from the
    service to the address
  */
  const validateAddress = useCallback(async () => {

    try {

      const userInputAddress = name ? getValues(name) : getValues();

      const userInputStreet = userInputAddress.street;

      const userInputStreet2 = userInputAddress.street2;

      const userInputCity = userInputAddress.city;

      const userInputState = userInputAddress.state;

      const userInputZip = userInputAddress.zip;

      const userInputCountry = userInputAddress.country;

      const geocodingResult = await geocodeAddress(userInputStreet, userInputCity, userInputState, userInputZip, userInputCountry, userInputStreet2);

      if (!geocodingResult) {

        setValue(`${name ? `${name}.lat` : 'lat'}`, undefined);

        setValue(`${name ? `${name}.lng` : 'lng'}`, undefined);

        setValue(`${name ? `${name}.addressFormatted` : 'addressFormatted'}`, undefined);

        setValue(`${name ? `${name}.addressValidated` : 'addressValidated'}`, false);

        setPois([]);

      } else {

        setValue(`${name ? `${name}.lat` : 'lat'}`, geocodingResult.geometry.location.lat());

        setValue(`${name ? `${name}.lng` : 'lng'}`, geocodingResult.geometry.location.lng());

        setValue(`${name ? `${name}.addressFormatted` : 'addressFormatted'}`, geocodingResult.formatted_address);

        setValue(`${name ? `${name}.addressValidated` : 'addressValidated'}`, true);

        const addressComponents = geocodingResult.address_components || [];

        const stateComponent = addressComponents.find((component) => component.types.includes('administrative_area_level_1'));

        if (stateComponent) {

          setValue(`${name ? `${name}.stateCode` : 'stateCode'}`, stateComponent.short_name);

          setValue(`${name ? `${name}.state` : 'state'}`, stateComponent.long_name);

        }

        const countryComponent = addressComponents.find((component) => component.types.includes('country'));

        if (countryComponent) {

          setValue(`${name ? `${name}.countryCode` : 'countryCode'}`, countryComponent.short_name);

          setValue(`${name ? `${name}.country` : 'country'}`, countryComponent.long_name);

        }

        const poi: Poi = {

          key: geocodingResult.formatted_address,

          location: {

            lat: geocodingResult.geometry.location.lat(),

            lng: geocodingResult.geometry.location.lng()

          }

        }

        setPois([poi]);

      }

      setGeocodingResult(geocodingResult);

    } catch (error) {

      console.log(error);

    }

  }, [name, setValue, getValues, geocodeAddress, setGeocodingResult]);

  const validateAddressDebounced = useMemo(() => debounce(validateAddress, 500), [validateAddress]);

  /*
    Validat address when street, street2, city, state, zip, or country values change
    and are not undefined or empty strings
  */
  useEffect(() => {

    if (country !== 'United States' && country !== 'Canada') {

      if (![street, city, zip, country].includes(undefined) && ![street, city, zip, country].includes('')) {

        validateAddressDebounced();

      } else {

        setGeocodingResult(undefined);

      }

    } else {

      if (![street, city, state, zip, country].includes(undefined) && ![street, city, state, zip, country].includes('')) {

        validateAddressDebounced();

      } else {

        setGeocodingResult(undefined);

      }

    }

  }, [street, street2, city, state, zip, country, validateAddressDebounced]);

  return (

    <Stack direction="column" spacing={2}>

      {!addressBook &&

        <SubHeading>

          {label}

        </SubHeading>

      }

      <TextField name={name ? `${name}.street` : 'street'} label="Street Address" />

      <TextField name={name ? `${name}.street2` : 'street2'} label="Address Line 2" />

      <SplitColumnWrapper>

        <TextField name={name ? `${name}.city` : 'city'} label="City" />

        <StateFieldAutoComplete
          options={countryCode ? countryCode === 'US' ? USStates : CanadaStates : [...USStates, ...CanadaStates]}
          name={name ? `${name}.state` : 'state'}
          label={'State / Province'}
          hidden={countryCode && countryCode !== 'US' && countryCode !== 'CA'} />

      </SplitColumnWrapper>

      <SplitColumnWrapper>

        <TextField name={name ? `${name}.zip` : 'zip'} label="Postal Code" />

        <CountryFieldAutoComplete
          options={Countries}
          name={name ? `${name}.country` : 'country'}
          label={'Country'} />

      </SplitColumnWrapper>

      {showPhone &&

        <TextField name={name ? `${name}.phone` : 'phone'} label="Phone Number (Optional)" />

      }

      {(geoCodingResult?.geometry && showGoogleMapValidation) &&

        <>

          <Map
            defaultZoom={14}
            mapId="user_address"
            style={{ width: '100%', height: '400px' }}
            center={geoCodingResult?.geometry.location}
            defaultCenter={{ lat: -33.860664, lng: 151.208138 }}
            disableDefaultUI={true}>

            <Markers pois={pois} />


          </Map>

          {showValidateMapWarning &&

            <Typography fontWeight="bold">Please review above map and confirm the correct address information.  If map location is incorrect please adjust address accordingly.</Typography>

          }

        </>

      }

    </Stack>

  )

};

interface MarkersProps {

  pois: Poi[];

};

type Poi = {

  key: string,

  location: google.maps.LatLngLiteral

}


const Markers: FC<MarkersProps> = ({ pois }) => {

  return (

    <>

      {pois.map((poi: Poi) => (

        <AdvancedMarker
          key={poi.key}
          position={poi.location}
          title={'Address'}
          clickable={true}>
          <Pin glyph="" background={'#cb2a20'} glyphColor={'#FFF'} borderColor={'#000'} />

        </AdvancedMarker>

      ))}

    </>

  )

}

export default AddressField;
